import {Card, Flex, Input, Segmented, Space, Tabs} from "antd";
import React, {useState} from "react";
import {generateSynopsisPrompt} from "./prompts/PlotPrompts";
import {generateActPrompt, generateActsPrompt, improveActPrompt} from "./prompts/PlotActsPrompts";
import {generatePlotLinePrompt} from "./prompts/PlotLinesPrompts";
import {faArrowUpWideShort, faRotate} from "@fortawesome/free-solid-svg-icons";
import {aiChainPrompt, similaritySearch} from "../../../../../../ai/Prompt";
import {useAppDispatch, useAppSelector} from "../../../../../../feature/tools";
import {
    resetDraft, updateAct,
    updateActs, updateLine,
    updateLinesContent,
    updateSynopsis,
    updateTitle
} from "../../../../../../feature/project/draftSlice";
import {GenButton} from "../../../../../../ai/components/GenButton";
import {TextAreaActions} from "../../../../../../ai/components/TextAreaActions";
import {DebounceInput} from "../../../../../../components/DebounceInput";
import {IconBoltGrad} from "../../../../../../components/icons/faBoltGrad";
import {BoltIcon} from "../../../../../../ai/components/BoltIcons";

export const PlotDetails = (props: {}) => {
    const dispatch = useAppDispatch()
    const draft = useAppSelector(state => state.draftSlice.draft)
    const [tab, setTab] = useState<string>('acts')

    const generateActs = async (model: string, projectId: string) => {
        let context = await similaritySearch({
            project: projectId,
            query: draft.properties.synopsis ?? '',
            where: {"$or": [{'type': 'lore'}, {'type': 'setting'}, {'type': 'shot'}]}
        })
        let data = await aiChainPrompt({
            projectId: projectId,
            prompt:
                generateActsPrompt({
                    genres: draft.properties.genre,
                    synopsis: draft.properties.synopsis,
                    context: context?.documents.flat()
                })
            ,
            model: model,
            type: 'json'
        })
        dispatch(updateActs({
            acts: data.data.map((act: any) => {
                return {...act, chapters: []}
            })
        }))
    }


    const generateSynopsis = async (model: string, projectId: string) => {
        let data = await aiChainPrompt({
            projectId: projectId ?? '',
            prompt:
                generateSynopsisPrompt({
                    genres: draft.properties.genre,
                    idea: draft.properties.synopsis,
                })
            ,
            model: model,
            type: 'json'
        })
        dispatch(updateTitle({
            title: data.data.title
        }))
        dispatch(updateSynopsis({
            text: data.data.synopsis
        }))
    }


    const generateLines = async (model: string, projectId: string) => {
        if (draft.properties.lines < 2) {
            return
        }
        let lines: { id: number, title: string, description: string } [] = []
        for (let i = 0; i < draft.properties.lines; i++) {
            let otherLines = lines.map((line, index) => {
                return {line: line.id, title: line.title, content: line.description}
            })
            let context = await similaritySearch({
                project: projectId,
                query: draft.properties.synopsis,
                where: {"$or": [{'type': 'lore'}, {'type': 'setting'}, {'type': 'shot'}]}
            })
            let data = await aiChainPrompt({
                projectId: projectId,
                prompt:
                    generatePlotLinePrompt({
                        lineNumber: i + 1,
                        genres: draft.properties.genre,
                        synopsis: draft.properties.synopsis,
                        otherLines: otherLines,
                        context: context?.documents
                    }),
                model: model,
                type: 'json'
            })
            lines.push(data.data)
        }
        dispatch(updateLinesContent({lines: lines}))
    }

    return (
        <Flex style={{padding: 4, flexGrow: 1, height: "100%", overflowY: 'auto', scrollbarWidth: "none"}}
              vertical={true} gap={10}>
            <Card title={<Flex justify={'space-between'} align={'center'}>Synopsis
                <Space>
                    <GenButton
                        style={{width: 130}}
                        type={'primary'}
                        actions={[{
                            key: "1", title: "Generate", action: async (model: string, projectId: string) => {
                                dispatch(resetDraft({resetTitle: true}))
                                await generateSynopsis(model, projectId)
                            }
                        }]}/>
                </Space>
            </Flex>}>

                <TextAreaActions
                    style={{border: "none"}}
                    value={draft.properties.synopsis}
                    autoSize={true}
                    validate={text => true}
                    onChange={(text: string) => {
                        dispatch(updateSynopsis({text: text}))
                    }}
                />
            </Card>
            <Card>
                <Flex justify={'space-between'}>
                    <Segmented
                        options={
                            [
                                {
                                    label: "Acts",
                                    value: 'acts',
                                },
                                {
                                    label: "Lines",
                                    value: 'lines',
                                }
                            ]
                        }
                        onChange={(value) => {
                            setTab(value)
                        }}
                        value={tab}/>
                    <GenButton
                        style={{width: 130}}
                        type={'primary'}
                        actions={[{
                            key: "1", title: "Generate", action: async (model: string, projectId: string) => {
                                if (tab === "acts") {
                                    dispatch(resetDraft({resetTitle: false}))
                                    await generateActs(model, projectId)
                                }
                            }
                        }]
                        }/>
                </Flex>
            </Card>
            {tab == 'acts' ? <Acts/> : <Lines/>}
        </Flex>
    )
}

export const Acts = () => {
    const dispatch = useAppDispatch()
    const draft = useAppSelector(state => state.draftSlice.draft)

    const regenerateAct = async (model: string, act: any, projectId: string) => {
        let context = await similaritySearch({
            project: projectId,
            query: act.description ?? '',
            where: {"$or": [{'type': 'lore'}, {'type': 'setting'}, {'type': 'shot'}]}
        })
        let data = await aiChainPrompt({
            projectId: projectId,
            prompt: generateActPrompt({
                genres: draft.properties.genre,
                synopsis: draft.properties.synopsis,
                act: act.id,
                acts: draft.story.acts,
                context: context?.documents
            }),
            model: model,
            type: 'json'
        })
        dispatch(updateAct({
            act: {
                ...act,
                title: data.data.title,
                description: data.data.description
            }
        }))
    }

    const improveAct = async (model: string, act: any, projectId: string) => {
        let context = await similaritySearch({
            project: projectId,
            query: act.description ?? '',
            where: {"$or": [{'type': 'lore'}, {'type': 'setting'}, {'type': 'shot'}]}
        })
        let data = await aiChainPrompt({
            projectId: projectId,
            prompt: improveActPrompt({
                genres: draft.properties.genre,
                synopsis: draft.properties.synopsis,
                act: act.id,
                acts: draft.story.acts,
                context: context?.documents
            }),
            model: model,
            type: 'json'
        })
        dispatch(updateAct({
            act: {
                ...act,
                title: data.data.title,
                description: data.data.description
            }
        }))
    }

    return (
        <Space direction={'vertical'}
               style={{scrollbarWidth: "none", width: "100%"}}>
            {draft.story.acts.map((item, index) =>
                <Card style={{flexGrow: 1}} key={index}
                      title={<Flex gap={10} justify={'space-between'} style={{flexGrow: 1}}>
                          <div style={{
                              height: 30,
                              width: 30,
                              minWidth: 30,
                              borderRadius: 30,
                              background: "#fff",
                              color: "#000",
                              textAlign: "center",
                              padding: 3
                          }}>{index + 1}</div>
                          <DebounceInput
                              style={{fontWeight: 'bold'}}
                              placeholder={"Scene title"}
                              value={item.title}
                              onChange={(text) => {
                                  dispatch(updateAct({
                                      // @ts-ignore
                                      act: {
                                          ...item,
                                          title: text
                                      }
                                  }))
                              }}/>
                          <GenButton
                              mode={'icon'}
                              hideMainAction={true}
                              icon={<IconBoltGrad/>}
                              actions={[{
                                  key: "1",
                                  title: "Re-generate",
                                  action: async (model: string, projectId: string) => regenerateAct(model, item, projectId),
                                  render: (key, title, action) => {
                                      return <Space><BoltIcon secondary={faRotate} size={16}/>{title}</Space>
                                  }
                              }, {
                                  key: "2",
                                  title: "Improve",
                                  action: async (model: string, projectId: string) => improveAct(model, item, projectId),
                                  render: (key, title, action) => {
                                      return <Space><BoltIcon secondary={faArrowUpWideShort} size={16}/>{title}
                                      </Space>
                                  }
                              }]}/>
                      </Flex>}>

                    <TextAreaActions
                        style={{border: "none"}}
                        value={item.description}
                        placeholder={"Short content"}
                        autoSize={true}
                        validate={(text: string) => true}
                        onChange={(text: string) => {
                            dispatch(updateAct({
                                // @ts-ignore
                                act: {
                                    ...item,
                                    description: text
                                }
                            }))
                        }}
                    />
                </Card>
            )}
        </Space>
    )
}

export const Lines = () => {
    const dispatch = useAppDispatch()
    const draft = useAppSelector(state => state.draftSlice.draft)

    return (
        <Space direction={'vertical'}
               style={{scrollbarWidth: "none", width: "100%"}}>
            {draft.story.lines?.map((item, index) =>
                <Card style={{flexGrow: 1}} key={index}
                      title={<Flex gap={10} style={{flexGrow: 1}}>
                          <div style={{
                              height: 30,
                              width: 30,
                              borderRadius: 30,
                              background: "#eee",
                              textAlign: "center",
                              padding: 3
                          }}>{index + 1}</div>
                          <DebounceInput
                              style={{fontWeight: 'bold'}}
                              placeholder={"Scene title"}
                              value={item.title}
                              onChange={(text) => {
                                  dispatch(updateLine({
                                      // @ts-ignore
                                      line: {
                                          ...item,
                                          title: text
                                      }
                                  }))
                              }}/>
                          <GenButton
                              type={'text'}
                              mode={'icon'}
                              actions={[{
                                  key: "1",
                                  title: "Generate",
                                  action: async (model: string, projectId: string) => {
                                      let otherLines = draft.story.lines.filter((l: any) => l.id != item.id).map((line, index) => {
                                          return {
                                              line: line.id,
                                              title: line.title ?? '',
                                              content: line.description ?? ''
                                          }
                                      })
                                      let context = await similaritySearch({
                                          project: projectId,
                                          query: item.description ?? '',
                                          where: {"$or": [{'type': 'lore'}, {'type': 'setting'}, {'type': 'shot'}]}
                                      })
                                      let data = await aiChainPrompt({
                                          projectId: projectId,
                                          prompt: generatePlotLinePrompt({
                                              lineNumber: item.id,
                                              genres: draft.properties.genre,
                                              synopsis: draft.properties.synopsis,
                                              otherLines: otherLines,
                                              context: context?.documents
                                          }),
                                          model: model,
                                          type: 'json'
                                      })
                                      dispatch(updateLine({
                                          line: {
                                              ...item,
                                              title: data.data.title,
                                              description: data.data.description
                                          }
                                      }))
                                  }
                              }]}/>
                      </Flex>}>
                    <TextAreaActions
                        style={{border: "none"}}
                        value={item.description}
                        placeholder={"Short content"}
                        autoSize={true}
                        validate={(text: string) => true}
                        onChange={(text: string) => {
                            dispatch(updateLine({
                                // @ts-ignore
                                line: {
                                    ...item,
                                    description: text
                                }
                            }))
                        }}
                    />
                </Card>
            )}
        </Space>
    )
}
